/**
 * Input Validation Utilities
 * 
 * Centralized validation functions for form inputs across FleetSkipper
 * Provides consistent error messages and validation rules
 */

import { debug } from './logging';

/**
 * Validation rule definitions
 */
export const VALIDATION_RULES = {
  REQUIRED_TEXT_FIELDS: ['name', 'details', 'notes', 'description', 'title'],
  MIN_TEXT_LENGTH: 2,
  MAX_TEXT_LENGTH: 500,
  MAX_TEXTAREA_LENGTH: 2000
};

/**
 * Check if a field name indicates it should be required
 */
const isRequiredField = (fieldName) => {
  return VALIDATION_RULES.REQUIRED_TEXT_FIELDS.some(keyword => 
    fieldName.toLowerCase().includes(keyword)
  );
};

/**
 * Validate a single field
 */
export const validateField = (fieldName, value, rules = {}) => {
  const errors = [];
  const trimmedValue = typeof value === 'string' ? value.trim() : value;
  
  // Required field validation
  if (rules.required || isRequiredField(fieldName)) {
    if (!trimmedValue || trimmedValue === '' || trimmedValue === 'Select option' || trimmedValue === 'Choose...') {
      errors.push(`${fieldName} is required`);
    }
  }
  
  // Text length validation
  if (typeof trimmedValue === 'string' && trimmedValue.length > 0) {
    if (rules.minLength && trimmedValue.length < rules.minLength) {
      errors.push(`${fieldName} must be at least ${rules.minLength} characters`);
    }
    
    if (rules.maxLength && trimmedValue.length > rules.maxLength) {
      errors.push(`${fieldName} cannot exceed ${rules.maxLength} characters`);
    }
    
    // Default length limits if not specified
    if (!rules.maxLength) {
      const defaultMax = fieldName.toLowerCase().includes('note') || fieldName.toLowerCase().includes('description') 
        ? VALIDATION_RULES.MAX_TEXTAREA_LENGTH 
        : VALIDATION_RULES.MAX_TEXT_LENGTH;
      
      if (trimmedValue.length > defaultMax) {
        errors.push(`${fieldName} cannot exceed ${defaultMax} characters`);
      }
    }
  }
  
  // Date validation
  if (rules.type === 'date') {
    if (trimmedValue && !isValidDate(trimmedValue)) {
      errors.push(`${fieldName} must be a valid date`);
    }
    
    if (rules.maxDate && trimmedValue) {
      const inputDate = new Date(trimmedValue);
      const maxDate = new Date(rules.maxDate);
      if (inputDate > maxDate) {
        errors.push(`${fieldName} cannot be in the future`);
      }
    }
  }
  
  // Email validation
  if (rules.type === 'email' && trimmedValue) {
    if (!isValidEmail(trimmedValue)) {
      errors.push(`${fieldName} must be a valid email address`);
    }
  }
  
  // Dropdown validation
  if (rules.type === 'dropdown' && trimmedValue) {
    if (trimmedValue === '' || trimmedValue.startsWith('Select') || trimmedValue.startsWith('Choose')) {
      errors.push(`Please select a ${fieldName.toLowerCase()}`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate an entire form object
 */
export const validateForm = (formData, validationSchema = {}) => {
  const allErrors = {};
  let isFormValid = true;
  
  // Validate each field according to schema
  Object.keys(validationSchema).forEach(fieldName => {
    const fieldValue = formData[fieldName];
    const fieldRules = validationSchema[fieldName];
    
    const validation = validateField(fieldName, fieldValue, fieldRules);
    
    if (!validation.isValid) {
      allErrors[fieldName] = validation.errors;
      isFormValid = false;
    }
  });
  
  // Auto-detect required fields if no schema provided
  if (Object.keys(validationSchema).length === 0) {
    Object.keys(formData).forEach(fieldName => {
      if (isRequiredField(fieldName)) {
        const validation = validateField(fieldName, formData[fieldName], { required: true });
        if (!validation.isValid) {
          allErrors[fieldName] = validation.errors;
          isFormValid = false;
        }
      }
    });
  }
  
  debug.log('Form validation result:', { isFormValid, errors: allErrors });
  
  return {
    isValid: isFormValid,
    errors: allErrors,
    errorCount: Object.keys(allErrors).length
  };
};

/**
 * Helper validation functions
 */
export const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidTime = (timeString) => {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(timeString);
};

/**
 * Number validation for maritime safety compliance
 * Prevents dangerous silent conversion of invalid input to 0
 */
export const validatePositiveInteger = (value, fieldName, options = {}) => {
  const { min = 0, max = 999999, allowEmpty = false } = options;
  
  // Handle empty values
  if (value === '' || value === null || value === undefined) {
    if (allowEmpty) return { valid: true, value: '' };
    return { valid: false, error: `${fieldName} is required` };
  }
  
  // Parse the number
  const parsed = parseInt(value, 10);
  
  // Check if valid number
  if (isNaN(parsed)) {
    return { valid: false, error: `${fieldName} must be a valid number` };
  }
  
  // Range checks
  if (parsed < min) {
    return { valid: false, error: `${fieldName} must be at least ${min}` };
  }
  
  if (parsed > max) {
    return { valid: false, error: `${fieldName} must not exceed ${max}` };
  }
  
  return { valid: true, value: parsed };
};

// For decimal numbers (gross tonnage, etc)
export const validatePositiveNumber = (value, fieldName, options = {}) => {
  const { min = 0, max = 999999, allowEmpty = false, decimals = 2 } = options;
  
  if (value === '' || value === null || value === undefined) {
    if (allowEmpty) return { valid: true, value: '' };
    return { valid: false, error: `${fieldName} is required` };
  }
  
  const parsed = parseFloat(value);
  
  if (isNaN(parsed)) {
    return { valid: false, error: `${fieldName} must be a valid number` };
  }
  
  if (parsed < min) {
    return { valid: false, error: `${fieldName} must be at least ${min}` };
  }
  
  if (parsed > max) {
    return { valid: false, error: `${fieldName} must not exceed ${max}` };
  }
  
  // Round to specified decimals
  const rounded = Math.round(parsed * Math.pow(10, decimals)) / Math.pow(10, decimals);
  
  return { valid: true, value: rounded };
};

/**
 * Get user-friendly error message for display
 */
export const getErrorMessage = (errors) => {
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }
  
  const errorCount = Object.keys(errors).length;
  const firstError = Object.values(errors)[0][0]; // Get first error message
  
  if (errorCount === 1) {
    return firstError;
  } else {
    return `Please check required fields and try again. (${errorCount} errors found)`;
  }
};

/**
 * Validation schema templates for common forms
 */
export const VALIDATION_SCHEMAS = {
  DEFECT_RECORD: {
    description: { required: true, minLength: 10, maxLength: 1000 },
    date_reported: { required: true, type: 'date', maxDate: new Date().toISOString().split('T')[0] },
    time_reported: { required: true, type: 'time' },
    reported_by: { required: true, minLength: 2 },
    assigned_to: { required: true, minLength: 2 },
    status: { required: true, type: 'dropdown' }
  },
  
  DRILL_RECORD: {
    type: { required: true, type: 'dropdown' },
    date: { required: true, type: 'date', maxDate: new Date().toISOString().split('T')[0] },
    time: { required: true, type: 'time' },
    duration: { required: true, minLength: 1 }
  },
  
  CREW_MEMBER: {
    name: { required: true, minLength: 2, maxLength: 100 },
    position: { minLength: 2, maxLength: 100 }
  }
};

export default {
  validateField,
  validateForm,
  getErrorMessage,
  VALIDATION_SCHEMAS,
  isValidDate,
  isValidEmail,
  isValidTime
};